import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useCartData, useSessionData, useServiceablitityData } from './order';
import { getBaseUrl, getSessionOrderAccessToken } from '../common/common';
import { appendInvocaTestModeParam } from '../util/invocaTestMode';
import { CheckScheduleAppt, setTFN, setRtsVisible, setInstallType } from '../store/action/OrderAction';
import { MOBILE_ABND_TFN, WHI_ABND_TFN } from '../util/util';

// Shared by the multi-step checkout (CheckOut.js) and the single-page checkout
// (SinglePageCheckout.js), so the session guard, bundle sync and TFN/schedule/RTS rules only
// live in one place.

// Sends the customer home when the cart, session or order access token is missing (the redux
// store is not persisted, so a reload lands here). Runs once on mount.
export const useCheckoutSessionGuard = () => {
  const { sessionResponse } = useSessionData();
  const { isProductsAdded } = useCartData();

  useEffect(() => {
    if (!isProductsAdded || Object.keys(sessionResponse).length === 0 || !getSessionOrderAccessToken()) {
      window.location.href = appendInvocaTestModeParam(getBaseUrl());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

// Creates/updates the selected bundle when products are selected individually.
export const useCheckoutBundle = () => {
  const dispatch = useDispatch();
  const {
    isInternetAdded,
    isMobileAdded,
    isVasAdded,
    isBundleAdded,
    internetPlan,
    wirelessCartInfo,
    vasPlanInfo,
    servRef,
    updateSelectedBundle,
  } = useCartData();
  const { serviceabilityCheck } = useServiceablitityData();
  const { bundleData } = serviceabilityCheck;

  // updateSelectedBundle is recreated on every render, so it is deliberately left out of the
  // deps - including it would dispatch setSelectedBundle on every render.
  useEffect(() => {
    updateSelectedBundle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isInternetAdded,
    isMobileAdded,
    isVasAdded,
    isBundleAdded,
    bundleData,
    dispatch,
    internetPlan,
    wirelessCartInfo,
    vasPlanInfo,
    servRef,
  ]);
};

// The single-page checkout offers no install-type choice: a CSI (address-contingent) plan is
// always self install there, never professional. Setting installType rather than adding a
// second flag reuses everything the legacy CSI flow (ELNKWEB-2948) already derives from it -
// isSelfInstall suppresses scheduling and clears a saved appointment (SET_INSTALL_TYPE),
// relabels the one-time charge, swaps in the CSI terms and activation copy, and sends csiOrder.
// SAVE_PLAN_INFO clears installType whenever the plan changes, so this re-applies it after
// every switch to a CSI plan; a switch to a non-CSI plan is left cleared and stays professional.
export const useCsiSelfInstallDefault = () => {
  const dispatch = useDispatch();
  const { isCsiEligible, installType } = useCartData();

  useEffect(() => {
    if (isCsiEligible && installType !== 'self') {
      dispatch(setInstallType('self'));
    }
  }, [dispatch, isCsiEligible, installType]);
};

// Sets the abandoned-cart TFN, then checks install windows: opens the schedule step when
// there are windows, otherwise skips ahead and shows the RTS modal.
export const initCheckoutTfnAndSchedule = ({
  dispatch,
  plans = [],
  mobileProducts = [],
  fixedWirelessProducts = [],
  isInternetAdded,
  showSchedule,
  internetPlan,
  callLogId,
  isMobileAdded,
  isScheduleSuppressed,
  setActiveKey,
}) => {
  if (!plans.length && !mobileProducts.length && fixedWirelessProducts.length) {
    dispatch(setTFN(WHI_ABND_TFN));
  } else if (!isInternetAdded) {
    dispatch(setTFN(MOBILE_ABND_TFN));
  } else {
    dispatch(setTFN('ABNDCART'));
  }

  if (showSchedule) {
    dispatch(CheckScheduleAppt({
      userName: 'xmlc',
      serviceReference: internetPlan.servRef,
      callLogId,
    })).then((res) => {
      if (res.value.data.installWindow && res.value.data.installWindow.length > 0) {
        setActiveKey('1');
      } else {
        setActiveKey(isMobileAdded ? '2' : '3');
        if (!isScheduleSuppressed) {
          dispatch(setRtsVisible(true));
        }
      }
    // Any rejection opens RTS. The status code cannot tell "no windows" from an outage
    // (the XMLc path returns 404/500, the Midas proxy maps every failure to 400), and
    // letting the order through would submit a professional install with no installWindow.
    }).catch(() => {
      setActiveKey(isMobileAdded ? '2' : '3');
      if (!isScheduleSuppressed) {
        dispatch(setRtsVisible(true));
      }
    });
  } else {
    setActiveKey(isMobileAdded ? '2' : '3');
  }
};
